import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { supabaseAdmin } from "@/integrations/supabase/client.server";
import { recordTemplateSignal } from "./template-proposals.functions";

// List program templates available to user (official + their own community)
export const listProgramTemplates = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { supabase } = context;
    const { data, error } = await supabase
      .from("program_templates")
      .select("id,name,logo_url,revenue_model_default,usage_count,status,default_csv_schema_json,default_report_slots_json,default_config_json")
      .order("status", { ascending: true })
      .order("usage_count", { ascending: false });
    if (error) throw new Error(error.message);
    return { templates: data ?? [] };
  });

// User's accessible accounts and workspace counts
export const listMyAccounts = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { supabase, userId } = context;
    const { data: members, error } = await supabase
      .from("account_members")
      .select("account_id, role, accounts(id,name,plan,plan_status,workspace_limit,trial_ends_at)")
      .eq("user_id", userId);
    if (error) throw new Error(error.message);
    const accounts = (members ?? []).map((m: any) => ({ ...m.accounts, role: m.role }));
    // workspace counts
    const ids = accounts.map((a: any) => a.id);
    let counts: Record<string, number> = {};
    if (ids.length) {
      const { data: ws } = await supabase.from("workspaces").select("account_id").in("account_id", ids);
      (ws ?? []).forEach((w: any) => { counts[w.account_id] = (counts[w.account_id] ?? 0) + 1; });
    }
    return { accounts: accounts.map((a: any) => ({ ...a, workspace_count: counts[a.id] ?? 0 })) };
  });

export const listMyWorkspaces = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { supabase } = context;
    const { data, error } = await supabase
      .from("workspaces")
      .select("id, account_id, program_name, revenue_model, is_active, sort_order, created_at")
      .order("sort_order", { ascending: true });
    if (error) throw new Error(error.message);
    return { workspaces: data ?? [] };
  });

const CreateWorkspaceInput = z.object({
  account_id: z.string().uuid(),
  program_name: z.string().min(1).max(120),
  revenue_model: z.enum(["recurring", "one_time", "mixed"]),
  program_template_id: z.string().uuid().nullable().optional(),
  csv_schema_json: z.record(z.string(), z.any()).default({}),
  config_json: z.record(z.string(), z.any()).default({}),
  report_slots: z
    .array(
      z.object({
        slot_name: z.string().min(1).max(120),
        slot_description: z.string().max(500).optional().nullable(),
        is_required_for_reconciliation: z.boolean().default(false),
        sort_order: z.number().int().default(0),
        is_custom: z.boolean().default(false),
      }),
    )
    .default([]),
});

export const createWorkspace = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) => CreateWorkspaceInput.parse(d))
  .handler(async ({ data, context }) => {
    const { supabase, userId } = context;

    // Verify membership and workspace limit
    const { data: account } = await supabase
      .from("accounts")
      .select("id, workspace_limit, plan, is_admin_comped")
      .eq("id", data.account_id)
      .single();
    if (!account) throw new Error("Account not found");
    const { count } = await supabase
      .from("workspaces")
      .select("*", { count: "exact", head: true })
      .eq("account_id", data.account_id);
    const { data: profile } = await supabase
      .from("profiles")
      .select("is_platform_admin")
      .eq("id", userId)
      .maybeSingle();
    const hasUnlimitedWorkspaces = !!account.is_admin_comped || !!profile?.is_platform_admin;

    if (!hasUnlimitedWorkspaces && (count ?? 0) >= account.workspace_limit) {
      const planLabel = account.plan.replace("_", " ");
      throw new Error(
        `Your ${planLabel} plan supports up to ${account.workspace_limit} workspaces. Upgrade at /settings/billing to add more.`,
      );
    }

    const { data: ws, error: wsErr } = await supabase
      .from("workspaces")
      .insert({
        account_id: data.account_id,
        program_name: data.program_name,
        revenue_model: data.revenue_model,
        program_template_id: data.program_template_id ?? null,
        csv_schema_json: data.csv_schema_json,
        config_json: data.config_json,
        sort_order: count ?? 0,
      })
      .select("id")
      .single();
    if (wsErr || !ws) throw new Error(wsErr?.message ?? "Failed to create workspace");

    if (data.report_slots.length) {
      const rows = data.report_slots.map((s, i) => ({
        workspace_id: ws.id,
        slot_name: s.slot_name,
        slot_description: s.slot_description ?? null,
        is_required_for_reconciliation: s.is_required_for_reconciliation,
        sort_order: s.sort_order ?? i,
      }));
      const { error: slErr } = await supabase.from("report_slots").insert(rows);
      if (slErr) throw new Error(slErr.message);

      // Custom slots → template learning signal (may produce a proposal at threshold)
      if (data.program_template_id) {
        const customSlots = data.report_slots.filter((s) => s.is_custom);
        for (const s of customSlots) {
          await recordTemplateSignal({
            programTemplateId: data.program_template_id,
            workspaceId: ws.id,
            accountId: data.account_id,
            signalType: "add_report_slot",
            signalKey: s.slot_name,
            signalValue: { slot_name: s.slot_name },
          });
        }
        // increment usage
        const { data: tpl } = await supabaseAdmin
          .from("program_templates")
          .select("usage_count")
          .eq("id", data.program_template_id)
          .single();
        if (tpl) {
          await supabaseAdmin
            .from("program_templates")
            .update({ usage_count: (tpl.usage_count ?? 0) + 1 })
            .eq("id", data.program_template_id);
        }
      }
    }

    await supabase.from("activity_events").insert({
      account_id: data.account_id,
      workspace_id: ws.id,
      actor_user_id: userId,
      event_type: "workspace_created",
      description: `Workspace "${data.program_name}" created`,
    });

    return { workspace_id: ws.id };
  });

const DeleteWorkspaceInput = z.object({
  workspace_id: z.string().uuid(),
  confirm_name: z.string().min(1),
});

export const deleteWorkspace = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) => DeleteWorkspaceInput.parse(d))
  .handler(async ({ data, context }) => {
    const { supabase, userId } = context;

    const { data: ws, error: wsErr } = await supabase
      .from("workspaces")
      .select("id, account_id, program_name")
      .eq("id", data.workspace_id)
      .single();
    if (wsErr || !ws) throw new Error("Workspace not found");

    if (ws.program_name.trim() !== data.confirm_name.trim()) {
      throw new Error("Confirmation name does not match workspace name");
    }

    const { data: mem } = await supabase
      .from("account_members")
      .select("role")
      .eq("account_id", ws.account_id)
      .eq("user_id", userId)
      .maybeSingle();
    if (!mem || (mem.role !== "owner" && mem.role !== "admin")) {
      throw new Error("Only account owners or admins can delete workspaces");
    }

    // Use admin client to cascade-delete all workspace-scoped data reliably
    const tables = [
      "segments",
      "payment_schedules",
      "global_contact_links",
      "activity_events",
      "transactions",
      "contacts",
      "report_slots",
      "anomalies",
      "imports",
      "contact_state_transitions",
      "push_queue",
      "integration_automations",
    ];
    for (const t of tables) {
      await (supabaseAdmin as any).from(t).delete().eq("workspace_id", data.workspace_id);
    }

    const { error: delErr } = await supabaseAdmin
      .from("workspaces")
      .delete()
      .eq("id", data.workspace_id);
    if (delErr) throw new Error(delErr.message);

    await supabase.from("activity_events").insert({
      account_id: ws.account_id,
      actor_user_id: userId,
      event_type: "workspace_deleted",
      description: `Workspace "${ws.program_name}" deleted`,
    });

    return { ok: true };
  });
